"use client";

import RTHMDirectLogo from "@/components/SVG/Logos/RTHM";
import { SidebarToggle } from "@/components/sidebar-toggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
    Bars3Icon,
    BeakerIcon,
    ChartBarSquareIcon,
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    PowerIcon,
    QuestionMarkCircleIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import { Calendar, HomeIcon, MoonIcon, PlusIcon, SunIcon } from "lucide-react";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useRef, useState } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface IntelligenceHeaderClientProps {
    session: Session | null;
}

export default function IntelligenceHeaderClient({
    session,
}: IntelligenceHeaderClientProps) {
    const isMobile = useIsMobile();
    const router = useRouter();
    const posthog = usePostHog();
    const [mounted, setMounted] = useState(false);
    const { theme, setTheme } = useTheme();
    const pathname = usePathname();

    const [bubbleStyle, setBubbleStyle] = useState({
        left: 0,
        width: 0,
        top: 0,
        height: 0,
        opacity: 0,
    });

    // Intelligence refs
    const dashboardRef = useRef<HTMLAnchorElement>(null);
    const chatRef = useRef<HTMLAnchorElement>(null);
    const recordsRef = useRef<HTMLAnchorElement>(null);
    const treatmentsRef = useRef<HTMLAnchorElement>(null);
    const supportRef = useRef<HTMLAnchorElement>(null);

    // Direct refs
    const directDashboardRef = useRef<HTMLAnchorElement>(null);
    const directTreatmentsRef = useRef<HTMLAnchorElement>(null);
    const directAppointmentsRef = useRef<HTMLAnchorElement>(null);
    const directSupportRef = useRef<HTMLAnchorElement>(null);

    // Avoid hydration mismatch by only rendering theme toggle after component is mounted
    useEffect(() => {
        setMounted(true);
    }, []);

    // Function to update the bubble style based on the active element
    const updateBubbleStyle = () => {
        if (!mounted) return;

        let activeElement: HTMLAnchorElement | null = null;

        // Intelligence paths
        if (isActive("/intelligence/dashboard") && dashboardRef.current) {
            activeElement = dashboardRef.current;
        } else if (isActive("/intelligence/chat") && chatRef.current) {
            activeElement = chatRef.current;
        } else if (
            isActive("/intelligence/assessment/metriport") &&
            recordsRef.current
        ) {
            activeElement = recordsRef.current;
        } else if (
            isActive("/intelligence/treatment-suggestions") &&
            treatmentsRef.current
        ) {
            activeElement = treatmentsRef.current;
        } else if (isActive("/intelligence/support") && supportRef.current) {
            activeElement = supportRef.current;
        }
        // Direct paths
        else if (isActive("/dashboard") && directDashboardRef.current) {
            activeElement = directDashboardRef.current;
        } else if (isActive("/treatments") && directTreatmentsRef.current) {
            activeElement = directTreatmentsRef.current;
        } else if (isActive("/appointments") && directAppointmentsRef.current) {
            activeElement = directAppointmentsRef.current;
        } else if (isActive("/support") && directSupportRef.current) {
            activeElement = directSupportRef.current;
        }

        if (activeElement) {
            const { offsetLeft, offsetWidth, offsetTop, offsetHeight } =
                activeElement;
            setBubbleStyle({
                left: offsetLeft,
                width: offsetWidth,
                top: offsetTop,
                height: offsetHeight,
                opacity: 1,
            });
        } else {
            // Optionally hide or move bubble if no known tab is active
            setBubbleStyle((prev) => ({ ...prev, opacity: 0 }));
        }
    };

    // Update bubble style when pathname or mounted state changes
    useEffect(() => {
        updateBubbleStyle();
    }, [pathname, mounted]);

    // Update bubble style when window is resized
    useEffect(() => {
        if (!mounted) return;

        const handleResize = () => {
            updateBubbleStyle();
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [mounted]);

    const profileInitial = session?.user?.first_name
        ? session.user.first_name[0].toLocaleUpperCase() +
          (session.user.last_name
              ? session.user.last_name[0].toLocaleUpperCase()
              : "")
        : session?.user?.email
          ? session.user.email[0].toLocaleUpperCase()
          : "U";

    const logOut = async () => {
        posthog?.capture("sign_out");
        posthog?.reset();
        document.cookie =
            "visitorId-discover=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
            "visitorId-intelligence=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        localStorage.removeItem("visitorId-discover");
        localStorage.removeItem("visitorId-intelligence");
        await signOut({ redirectTo: "/", redirect: true });
    };

    const isActive = (path: string) => {
        return pathname.startsWith(path);
    };

    return (
        <header className="flex sticky top-0 z-50">
            <div className="w-full px-4 lg:px-6 xl:px-8 py-3 flex items-center">
                {/* Left side: Fixed width section */}
                <div className="flex items-center md:w-[100px] justify-start">
                    {pathname.startsWith("/intelligence/chat") && (
                        <>
                            <SidebarToggle />
                            {!isMobile && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="ml-4 bg-white dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/50 hover:bg-blue-50 dark:hover:bg-blue-800/40"
                                            onClick={() => {
                                                router.push(
                                                    "/intelligence/chat",
                                                );
                                                router.refresh();
                                            }}
                                        >
                                            <PlusIcon size={18} />{" "}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent align="start">
                                        New Chat
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>

                {/* Pill Navigation - Centered */}
                <div className="flex justify-center flex-1">
                    <div className="bg-popover rounded-full px-4 py-2 flex items-center space-x-4 shadow-lg relative">
                        {/* Animated Bubble */}
                        <div
                            className="absolute rounded-full bg-primary/20 transition-all duration-700 ease-out pointer-events-none"
                            style={{
                                left: bubbleStyle.left,
                                width: bubbleStyle.width,
                                top: bubbleStyle.top,
                                height: bubbleStyle.height,
                                opacity: bubbleStyle.opacity,
                            }}
                        />
                        {/* RTHM Logo (desktop only) */}
                        <div className="hidden md:block mr-2">
                            <RTHMDirectLogo className="h-5 w-auto" />
                        </div>

                        {/* Intelligence Navigation */}
                        {pathname.includes("/intelligence") ? (
                            <>
                                {/* Intelligence Navigation Items */}
                                {/* Home Icon - Only shown when logged in */}
                                {session?.user && (
                                    <Link
                                        ref={dashboardRef}
                                        href="/intelligence/dashboard"
                                        onClick={() => {
                                            posthog?.capture(
                                                "intelligence_dashboard_from_header",
                                            );
                                        }}
                                        className={cn(
                                            "flex items-center justify-center p-2 rounded-full transition-colors z-10",
                                            isActive("/intelligence/dashboard")
                                                ? "text-primary"
                                                : "",
                                        )}
                                    >
                                        <HomeIcon className="h-5 w-5 md:mr-2" />
                                        <span className="hidden md:inline">
                                            Dashboard
                                        </span>
                                    </Link>
                                )}

                                {/* AI Chat - Only shown when logged in */}
                                {session?.user && (
                                    <Link
                                        ref={chatRef}
                                        href="/intelligence/chat"
                                        onClick={() => {
                                            posthog?.capture(
                                                "intelligence_ai_chat_page_from_header",
                                            );
                                        }}
                                        className={cn(
                                            "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                            isActive("/intelligence/chat")
                                                ? "text-primary font-medium"
                                                : "",
                                        )}
                                    >
                                        <ChatBubbleLeftRightIcon className="h-5 w-5 md:mr-2" />
                                        <span className="hidden md:inline">
                                            AI Chat
                                        </span>
                                    </Link>
                                )}

                                {/* Medical Records - Only shown when logged in */}
                                {session?.user && (
                                    <Link
                                        ref={recordsRef}
                                        href="/intelligence/assessment/metriport"
                                        onClick={() => {
                                            posthog?.capture(
                                                "intelligence_medical_records_from_header",
                                            );
                                        }}
                                        className={cn(
                                            "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                            isActive(
                                                "/intelligence/assessment/metriport",
                                            )
                                                ? "text-primary font-medium"
                                                : "",
                                        )}
                                    >
                                        <DocumentTextIcon className="h-5 w-5 md:mr-2" />
                                        <span className="hidden md:inline">
                                            Medical Records
                                        </span>
                                    </Link>
                                )}

                                {/* Treatments - Only shown when logged in */}
                                {session?.user && (
                                    <Link
                                        ref={treatmentsRef}
                                        href="/intelligence/treatment-suggestions"
                                        onClick={() => {
                                            posthog?.capture(
                                                "intelligence_treatment_suggestions_from_header",
                                            );
                                        }}
                                        className={cn(
                                            "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                            isActive(
                                                "/intelligence/treatment-suggestions",
                                            )
                                                ? "text-primary font-medium"
                                                : "",
                                        )}
                                    >
                                        <BeakerIcon className="h-5 w-5 md:mr-2" />
                                        <span className="hidden md:inline">
                                            Treatments
                                        </span>
                                    </Link>
                                )}

                                {/* Support */}
                                <Link
                                    ref={supportRef}
                                    href="/intelligence/support"
                                    onClick={() => {
                                        posthog?.capture(
                                            "support_page_from_header",
                                        );
                                    }}
                                    className={cn(
                                        "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                        isActive("/intelligence/support")
                                            ? "text-primary font-medium"
                                            : "",
                                    )}
                                >
                                    <QuestionMarkCircleIcon className="h-5 w-5 md:mr-2" />
                                    <span className="hidden md:inline">
                                        Support
                                    </span>
                                </Link>
                            </>
                        ) : (
                            <>
                                {/* Direct Navigation Items */}
                                {session?.user && (
                                    <>
                                        <Link
                                            ref={directDashboardRef}
                                            href="/dashboard"
                                            onClick={() => {
                                                posthog?.capture(
                                                    "dashboard_page_from_header",
                                                );
                                            }}
                                            className={cn(
                                                "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                                isActive("/dashboard")
                                                    ? "text-primary font-medium"
                                                    : "",
                                            )}
                                        >
                                            <HomeIcon className="h-5 w-5 md:mr-2" />
                                            <span className="hidden md:inline">
                                                Dashboard
                                            </span>
                                        </Link>
                                        <Link
                                            ref={directTreatmentsRef}
                                            href="/treatments"
                                            onClick={() => {
                                                posthog?.capture(
                                                    "treatments_page_from_header",
                                                );
                                            }}
                                            className={cn(
                                                "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                                isActive("/treatments")
                                                    ? "text-primary font-medium"
                                                    : "",
                                            )}
                                        >
                                            <BeakerIcon className="h-5 w-5 md:mr-2" />
                                            <span className="hidden md:inline">
                                                Treatments
                                            </span>
                                        </Link>
                                        <Link
                                            ref={directAppointmentsRef}
                                            href="/appointments"
                                            onClick={() => {
                                                posthog?.capture(
                                                    "appointments_page_from_header",
                                                );
                                            }}
                                            className={cn(
                                                "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                                isActive("/appointments")
                                                    ? "text-primary font-medium"
                                                    : "",
                                            )}
                                        >
                                            <Calendar className="h-5 w-5 md:mr-2" />
                                            <span className="hidden md:inline">
                                                Appointments
                                            </span>
                                        </Link>
                                    </>
                                )}

                                {/* Support for Direct */}
                                <Link
                                    ref={directSupportRef}
                                    href="/support"
                                    onClick={() => {
                                        posthog?.capture(
                                            "support_page_from_header",
                                        );
                                    }}
                                    className={cn(
                                        "px-3 py-1 rounded-full transition-colors flex items-center z-10",
                                        isActive("/support")
                                            ? "text-primary font-medium"
                                            : "",
                                    )}
                                >
                                    <QuestionMarkCircleIcon className="h-5 w-5 md:mr-2" />
                                    <span className="hidden md:inline">
                                        Support
                                    </span>
                                </Link>
                            </>
                        )}

                        {/* Profile */}
                        {session?.user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="p-0 h-auto"
                                    >
                                        <Avatar className="h-6 w-6 bg-rthm-dark-blue">
                                            <AvatarFallback className="text-xs font-medium bg-rthm-dark-blue text-white">
                                                {profileInitial}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    className="w-56 mt-1 border border-border/80 shadow-lg rounded-lg overflow-hidden"
                                >
                                    <DropdownMenuItem
                                        onClick={() =>
                                            setTheme(
                                                theme === "dark"
                                                    ? "light"
                                                    : "dark",
                                            )
                                        }
                                    >
                                        {mounted && theme === "dark" ? (
                                            <div className="flex items-center">
                                                <SunIcon className="w-5 h-5 mr-6 text-yellow-400" />
                                                Light Mode
                                            </div>
                                        ) : (
                                            <div className="flex items-center">
                                                <MoonIcon className="w-5 h-5 mr-6 text-foreground/80" />
                                                Dark Mode
                                            </div>
                                        )}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {session.user.canAccessDirect &&
                                        pathname !== "/dashboard" && (
                                            <DropdownMenuItem asChild>
                                                <Link
                                                    href="/dashboard"
                                                    onClick={() => {
                                                        posthog?.capture(
                                                            "dashboard_page_from_header",
                                                        );
                                                    }}
                                                    className="flex items-center cursor-pointer"
                                                >
                                                    <ChartBarSquareIcon className="w-5 h-5 mr-4 text-foreground/80" />
                                                    Direct Dashboard
                                                </Link>
                                            </DropdownMenuItem>
                                        )}
                                    {pathname !== "/intelligence/dashboard" && (
                                        <DropdownMenuItem asChild>
                                            <Link
                                                href="/intelligence/dashboard"
                                                onClick={() => {
                                                    posthog?.capture(
                                                        "intelligence_dashboard_page_from_header",
                                                    );
                                                }}
                                                className="flex items-center cursor-pointer"
                                            >
                                                <Bars3Icon className="w-5 h-5 mr-4 text-foreground/80" />
                                                Intelligence Dashboard
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem asChild>
                                        <Link
                                            href="/profile"
                                            onClick={() => {
                                                posthog?.capture(
                                                    "profile_page_from_header",
                                                );
                                            }}
                                            className="flex items-center cursor-pointer"
                                        >
                                            <UserIcon className="w-5 h-5 mr-4 text-foreground/80" />
                                            Profile
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={logOut}
                                        className="cursor-pointer"
                                    >
                                        <PowerIcon className="w-5 h-5 mr-4 text-foreground/80" />
                                        Sign Out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Button
                                id="log-in-from-header"
                                variant="ghost"
                                size="sm"
                                className="rounded-full text-sm font-medium"
                                onClick={() => {
                                    posthog?.capture("log_in_page_from_header");
                                    router.push(
                                        `/sign-in?redirectTo=${pathname}`,
                                    );
                                }}
                            >
                                <UserIcon className="h-5 w-5 md:mr-2" />
                                <span className="hidden md:inline">Log In</span>
                            </Button>
                        )}
                    </div>
                </div>

                {/* Right side: Fixed width section to balance the layout */}
                <div className="md:w-[100px]"></div>
            </div>
        </header>
    );
}
